import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import { useAccompagnements, Accompagnement, Session, Inscription } from '@/hooks/useAccompagnements'
import {ArrowLeft, LogOut, RefreshCw, Plus, Edit2, Trash2, X, Save, Users, Calendar as CalendarIcon, CalendarPlus, Euro, MapPin, Mail, Phone, Package, TrendingUp, AlertCircle, Clock, FileText, Sparkles} from 'lucide-react'
import DateTimePicker from '@/components/DateTimePicker'

type Tab = 'accompagnements' | 'sessions' | 'inscriptions' | 'planning'

const STATUT_COLORS: Record<string, string> = {
  ouverte: 'text-emerald-400 border-emerald-400/40',
  complete: 'text-amber-400 border-amber-400/40',
  annulee: 'text-red-400 border-red-400/40',
  terminee: 'text-[#8a8580] border-[#8a8580]/40',
}

const PAIEMENT_COLORS: Record<string, string> = {
  paye: 'text-emerald-400 border-emerald-400/40',
  en_attente: 'text-amber-400 border-amber-400/40',
  rembourse: 'text-blue-400 border-blue-400/40',
  annule: 'text-red-400 border-red-400/40',
}

const PAIEMENT_LABELS: Record<string, string> = {
  paye: 'Payé',
  en_attente: 'En attente',
  rembourse: 'Remboursé',
  annule: 'Annulé',
}

const STATUT_LABELS: Record<string, string> = {
  ouverte: 'Ouverte',
  complete: 'Complète',
  annulee: 'Annulée',
  terminee: 'Terminée',
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}

export default function AdminAccompagnements() {
  const { isAuthenticated, isAdmin, user } = useAuth()
  const {
    accompagnements, sessions, inscriptions, loading, fetchAll,
    createAccompagnement, updateAccompagnement, deleteAccompagnement,
    createSession, updateSession, deleteSession,
    updateInscription, deleteInscription,
  } = useAccompagnements()

  const [tab, setTab] = useState<Tab>(() => {
    if (typeof window === 'undefined') return 'accompagnements'
    const params = new URLSearchParams(window.location.search)
    const t = params.get('tab') as Tab | null
    return t && ['accompagnements', 'sessions', 'inscriptions', 'planning'].includes(t) ? t : 'accompagnements'
  })
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [editingAcc, setEditingAcc] = useState<Partial<Accompagnement> | null>(null)
  const [editingSession, setEditingSession] = useState<Partial<Session> | null>(null)
  const [editingInsc, setEditingInsc] = useState<Partial<Inscription> | null>(null)

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text })
    setTimeout(() => setToast(null), 2800)
  }

  const stats = useMemo(() => {
    const totalPaye = inscriptions.filter((i) => i.statutPaiement === 'paye').reduce((s, i) => s + (i.montant || 0), 0)
    const enAttente = inscriptions.filter((i) => i.statutPaiement === 'en_attente').length
    const sessionsOuvertes = sessions.filter((s) => s.statut === 'ouverte').length
    return {
      accompagnementsActifs: accompagnements.filter((a) => a.actif).length,
      sessionsOuvertes,
      inscrits: inscriptions.length,
      revenus: totalPaye,
      enAttente,
    }
  }, [accompagnements, sessions, inscriptions])

  if (!isAuthenticated) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center border border-[#c9a96e]/20 p-12 bg-gradient-to-br from-[#c9a96e]/5 to-transparent">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-[#c9a96e]/40 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-[#c9a96e]" />
          </div>
          <h2 className="font-serif text-3xl text-[#c9a96e] mb-6">Espace réservé</h2>
          <p className="text-[#8a8580] mb-8 font-light leading-relaxed">
            Gestion accessible uniquement aux administrateurs authentifiés.
          </p>
          <button
            onClick={() => lumi.auth.signIn()}
            className="inline-block px-8 py-3 border border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#0a0a0a] text-sm tracking-widest uppercase transition-all duration-500"
          >
            Se connecter
          </button>
        </div>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center border border-[#c9a96e]/20 p-12">
          <h2 className="font-serif text-3xl text-[#c9a96e] mb-6">Accès refusé</h2>
          <p className="text-[#8a8580] mb-4 font-light leading-relaxed">
            Votre compte n'a pas les permissions administrateur.
          </p>
          <p className="text-xs text-[#8a8580]/60 mb-8 font-light">
            Connecté : {user?.email || user?.userName}
          </p>
          <Link to="/" className="inline-block text-sm tracking-widest uppercase text-[#c9a96e] border-b border-[#c9a96e]/30 pb-2">
            Retour
          </Link>
        </div>
      </div>
    )
  }

  const handleSaveAcc = async () => {
    if (!editingAcc) return
    try {
      if (editingAcc._id) {
        await updateAccompagnement(editingAcc._id, editingAcc)
        showToast('success', 'Accompagnement mis à jour')
      } else {
        await createAccompagnement(editingAcc)
        showToast('success', 'Accompagnement créé')
      }
      setEditingAcc(null)
    } catch (e: any) {
      showToast('error', e?.message || 'Échec')
    }
  }

  const handleSaveSession = async (keepOpen: boolean = false) => {
    if (!editingSession) return
    try {
      if (!editingSession.accompagnementId) {
        showToast('error', 'Choisis un accompagnement')
        return
      }
      if (!editingSession.dateDebut) {
        showToast('error', 'Choisis une date de début')
        return
      }
      if (editingSession._id) {
        await updateSession(editingSession._id, editingSession)
        showToast('success', 'Date mise à jour')
        setEditingSession(null)
      } else {
        await createSession(editingSession)
        showToast('success', 'Date ajoutée')
        if (keepOpen) {
          // prépare un nouveau formulaire vide, même accompagnement
          const acc = accompagnements.find((a) => a._id === editingSession.accompagnementId)
          setEditingSession({
            accompagnementId: editingSession.accompagnementId,
            accompagnementNom: editingSession.accompagnementNom,
            lieu: editingSession.lieu || '',
            placesMax: acc?.placesMax || 1,
            placesRestantes: acc?.placesMax || 1,
            statut: 'ouverte',
          })
        } else {
          setEditingSession(null)
        }
      }
    } catch (e: any) {
      showToast('error', e?.message || 'Échec')
    }
  }

  const handleSaveInsc = async () => {
    if (!editingInsc || !editingInsc._id) return
    try {
      await updateInscription(editingInsc._id, editingInsc)
      showToast('success', 'Inscription mise à jour')
      setEditingInsc(null)
    } catch (e: any) {
      showToast('error', e?.message || 'Échec')
    }
  }

  const handleDelete = async (type: 'acc' | 'session' | 'insc', id: string, label: string) => {
    if (!window.confirm(`Supprimer ${label} ? Cette action est définitive.`)) return
    try {
      if (type === 'acc') await deleteAccompagnement(id)
      if (type === 'session') await deleteSession(id)
      if (type === 'insc') await deleteInscription(id)
      showToast('success', 'Supprimé')
    } catch (e: any) {
      showToast('error', e?.message || 'Échec')
    }
  }

  const sessionsPlanning = useMemo(() => {
    return [...sessions].sort((a, b) => new Date(a.dateDebut).getTime() - new Date(b.dateDebut).getTime())
  }, [sessions])

  return (
    <div className="min-h-[100vh] bg-[#0a0a0a]">
      {/* Top bar */}
      <div className="sticky top-20 z-40 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-[#c9a96e]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="inline-flex items-center gap-2 px-3 py-2 border border-[#c9a96e]/20 text-xs tracking-widest uppercase text-[#8a8580] hover:text-[#c9a96e] hover:border-[#c9a96e]/50 transition-all">
                <ArrowLeft className="w-3 h-3" /> Site
              </Link>
              <div>
                <h1 className="font-serif text-2xl lg:text-3xl text-[#f5f1ea] leading-tight">
                  Gestion des <span className="italic text-[#c9a96e]">accompagnements</span>
                </h1>
                <p className="text-xs text-[#8a8580] font-light mt-0.5">
                  Offres · Sessions · Inscrits · Planning
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/admin" className="inline-flex items-center gap-2 px-3 py-2 border border-[#c9a96e]/20 text-xs tracking-widest uppercase text-[#8a8580] hover:text-[#c9a96e] hover:border-[#c9a96e]/50 transition-all">
                <FileText className="w-3 h-3" /> Contenu
              </Link>
              <button onClick={fetchAll} disabled={loading} className="p-2.5 border border-[#c9a96e]/20 text-[#8a8580] hover:text-[#c9a96e] hover:border-[#c9a96e]/50 transition-all" title="Rafraîchir">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={() => lumi.auth.signOut()} className="p-2.5 border border-[#c9a96e]/20 text-[#8a8580] hover:text-red-400 hover:border-red-400/50 transition-all" title="Déconnexion">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed top-32 right-6 z-50 animate-[slideIn_0.3s_ease-out]">
          <div className={`px-6 py-4 border text-sm tracking-wide shadow-2xl ${
            toast.type === 'success' ? 'border-[#c9a96e]/60 text-[#c9a96e] bg-[#0a0a0a]' : 'border-red-500/60 text-red-400 bg-[#0a0a0a]'
          }`}>
            {toast.text}
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Package} label="Offres actives" value={stats.accompagnementsActifs} />
          <StatCard icon={CalendarIcon} label="Sessions ouvertes" value={stats.sessionsOuvertes} />
          <StatCard icon={Users} label="Inscrits" value={stats.inscrits} />
          <StatCard icon={Euro} label="Revenus (payés)" value={`${stats.revenus.toLocaleString('fr-FR')} CHF`} />
          <StatCard icon={AlertCircle} label="En attente" value={stats.enAttente} highlight={stats.enAttente > 0} />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-[#c9a96e]/10 mb-8">
          {([
            { k: 'accompagnements', l: 'Accompagnements', i: Package },
            { k: 'sessions', l: 'Sessions', i: CalendarIcon },
            { k: 'inscriptions', l: 'Inscrits', i: Users },
            { k: 'planning', l: 'Planning', i: TrendingUp },
          ] as const).map((t) => {
            const Icon = t.i
            const active = tab === t.k
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`inline-flex items-center gap-2 px-5 py-3 text-xs tracking-widest uppercase border-b-2 transition-all ${
                  active ? 'border-[#c9a96e] text-[#c9a96e]' : 'border-transparent text-[#8a8580] hover:text-[#f5f1ea]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.l}
              </button>
            )
          })}
        </div>

        {/* Tab content */}
        {tab === 'accompagnements' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl text-[#f5f1ea]">Offres proposées</h2>
              <button
                onClick={() => setEditingAcc({ actif: true, placesMax: 1, prix: 0, inclus: [] })}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#c9a96e] text-[#0a0a0a] hover:bg-[#d4b47a] text-xs tracking-widest uppercase font-semibold transition-all"
              >
                <Plus className="w-3.5 h-3.5" /> Nouvel accompagnement
              </button>
            </div>

            {accompagnements.length === 0 && (
              <div className="text-center py-16 border border-dashed border-[#c9a96e]/15">
                <Package className="w-8 h-8 text-[#8a8580] mx-auto mb-4" />
                <p className="text-[#8a8580] font-light">Aucun accompagnement pour l'instant</p>
              </div>
            )}

            <div className="grid gap-4">
              {accompagnements.map((a) => {
                const accSessions = sessions.filter((s) => s.accompagnementId === a._id)
                  .sort((x, y) => new Date(x.dateDebut).getTime() - new Date(y.dateDebut).getTime())
                const nbSessions = accSessions.length
                return (
                  <div key={a._id} className="border border-[#c9a96e]/15 p-5 hover:border-[#c9a96e]/40 transition-all">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-serif text-xl text-[#f5f1ea]">{a.nom}</h3>
                          {a.miseEnAvant && <span className="text-[10px] px-2 py-0.5 border border-[#c9a96e]/50 text-[#c9a96e] tracking-widest uppercase">Mis en avant</span>}
                          {!a.actif && <span className="text-[10px] px-2 py-0.5 border border-red-400/50 text-red-400 tracking-widest uppercase">Inactif</span>}
                        </div>
                        <p className="text-sm text-[#f5f1ea]/75 font-light leading-relaxed mb-3">{a.description}</p>
                        <div className="flex gap-4 flex-wrap text-xs text-[#8a8580]">
                          <span><Clock className="w-3 h-3 inline mr-1" />{a.duree}</span>
                          <span><Euro className="w-3 h-3 inline mr-1" />{a.prix} CHF</span>
                          <span><CalendarIcon className="w-3 h-3 inline mr-1" />{nbSessions} date{nbSessions > 1 ? 's' : ''}</span>
                          {a.lieu && <span><MapPin className="w-3 h-3 inline mr-1" />{a.lieu}</span>}
                        </div>
                        {a.instructionsPreparation && (
                          <p className="text-xs text-[#f5f1ea]/55 italic mt-2 font-light leading-relaxed line-clamp-2">
                            ✦ {a.instructionsPreparation}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => setEditingSession({
                            accompagnementId: a._id,
                            accompagnementNom: a.nom,
                            lieu: a.lieu || '',
                            placesMax: a.placesMax || 1,
                            placesRestantes: a.placesMax || 1,
                            statut: 'ouverte',
                          })}
                          className="inline-flex items-center gap-2 px-3 py-2 border border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#0a0a0a] text-[10px] tracking-widest uppercase font-semibold transition-all"
                          title="Ajouter une date"
                        >
                          <CalendarPlus className="w-3.5 h-3.5" /> Ajouter une date
                        </button>
                        <button onClick={() => setEditingAcc(a)} className="p-2 border border-[#c9a96e]/20 text-[#8a8580] hover:text-[#c9a96e] hover:border-[#c9a96e]/50 transition-all" title="Modifier">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => a._id && handleDelete('acc', a._id, a.nom)} className="p-2 border border-[#c9a96e]/20 text-[#8a8580] hover:text-red-400 hover:border-red-400/50 transition-all" title="Supprimer">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Liste des dates programmées pour cet accompagnement */}
                    <div className="mt-5 pt-4 border-t border-[#c9a96e]/10">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] tracking-widest uppercase text-[#8a8580]">
                          Dates programmées {nbSessions > 0 && <span className="text-[#c9a96e]">· {nbSessions}</span>}
                        </p>
                      </div>
                      {nbSessions === 0 ? (
                        <p className="text-xs text-[#8a8580]/60 italic font-light">
                          Aucune date pour l'instant. Utilise « Ajouter une date » pour en créer une (ou plusieurs).
                        </p>
                      ) : (
                        <div className="grid gap-2">
                          {accSessions.map((s) => {
                            const nbInscrits = inscriptions.filter((i) => i.sessionId === s._id).length
                            const isPast = new Date(s.dateDebut) < new Date()
                            return (
                              <div
                                key={s._id}
                                className={`flex items-center justify-between gap-3 px-3 py-2.5 border transition-all flex-wrap ${
                                  isPast ? 'border-[#8a8580]/15 opacity-60' : 'border-[#c9a96e]/15 hover:border-[#c9a96e]/40'
                                }`}
                              >
                                <div className="flex items-center gap-3 flex-wrap text-xs">
                                  <span className="text-[#f5f1ea]">
                                    <CalendarIcon className="w-3 h-3 inline mr-1 text-[#c9a96e]" />
                                    {formatDateTime(s.dateDebut)}
                                    {s.dateFin && <span className="text-[#8a8580]"> → {formatDateTime(s.dateFin)}</span>}
                                  </span>
                                  {s.lieu && <span className="text-[#8a8580]"><MapPin className="w-3 h-3 inline mr-1" />{s.lieu}</span>}
                                  <span className={`px-2 py-0.5 border tracking-widest uppercase text-[10px] ${STATUT_COLORS[s.statut]}`}>
                                    {STATUT_LABELS[s.statut]}
                                  </span>
                                  <span className="text-[#c9a96e]">
                                    <Users className="w-3 h-3 inline mr-1" />
                                    {s.placesMax - s.placesRestantes}/{s.placesMax} pris
                                  </span>
                                  {nbInscrits > 0 && (
                                    <span className="text-[#8a8580]">{nbInscrits} inscrit{nbInscrits > 1 ? 's' : ''}</span>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => setEditingSession(s)}
                                    className="p-1.5 border border-[#c9a96e]/20 text-[#8a8580] hover:text-[#c9a96e] hover:border-[#c9a96e]/50 transition-all"
                                    title="Modifier cette date"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => s._id && handleDelete('session', s._id, `la date du ${formatDate(s.dateDebut)}`)}
                                    className="p-1.5 border border-[#c9a96e]/20 text-[#8a8580] hover:text-red-400 hover:border-red-400/50 transition-all"
                                    title="Supprimer cette date"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
          </div>
          </div>
        )}

        {tab === 'sessions' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-serif text-xl text-[#f5f1ea]">Sessions programmées</h2>
                <p className="text-xs text-[#8a8580] font-light mt-1">
                  Les places se gèrent ici, date par date — tu peux en ajouter autant que tu veux sans toucher à l'accompagnement.
                </p>
              </div>
              <button
                onClick={() => setEditingSession({ statut: 'ouverte', placesMax: 1, placesRestantes: 1 })}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#c9a96e] text-[#0a0a0a] hover:bg-[#d4b47a] text-xs tracking-widest uppercase font-semibold transition-all"
              >
                <Plus className="w-3.5 h-3.5" /> Nouvelle date
              </button>
            </div>

            {accompagnements.length === 0 && (
              <div className="text-center py-16 border border-dashed border-[#c9a96e]/15">
                <Package className="w-8 h-8 text-[#8a8580] mx-auto mb-4" />
                <p className="text-[#8a8580] font-light">Crée d'abord un accompagnement dans l'onglet précédent.</p>
              </div>
            )}

            {/* Sessions regroupées par accompagnement */}
            <div className="space-y-5">
              {accompagnements.map((a) => {
                const accSessions = sessions.filter((s) => s.accompagnementId === a._id)
                  .sort((x, y) => new Date(x.dateDebut).getTime() - new Date(y.dateDebut).getTime())
                return (
                  <div key={a._id} className="border border-[#c9a96e]/15">
                    <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#c9a96e]/10 bg-gradient-to-r from-[#c9a96e]/5 to-transparent flex-wrap">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Package className="w-4 h-4 text-[#c9a96e]" />
                        <h3 className="font-serif text-lg text-[#f5f1ea]">{a.nom}</h3>
                        <span className="text-[10px] tracking-widest uppercase text-[#8a8580]">
                          {accSessions.length} date{accSessions.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <button
                        onClick={() => setEditingSession({
                          accompagnementId: a._id,
                          accompagnementNom: a.nom,
                          lieu: a.lieu || '',
                          placesMax: a.placesMax || 1,
                          placesRestantes: a.placesMax || 1,
                          statut: 'ouverte',
                        })}
                        className="inline-flex items-center gap-2 px-3 py-2 border border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#0a0a0a] text-[10px] tracking-widest uppercase font-semibold transition-all"
                      >
                        <CalendarPlus className="w-3.5 h-3.5" /> Ajouter une date
                      </button>
                    </div>

                    {accSessions.length === 0 ? (
                      <div className="px-5 py-6 text-center">
                        <p className="text-xs text-[#8a8580] font-light italic">
                          Aucune date programmée pour cet accompagnement.
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-[#c9a96e]/10">
                        {accSessions.map((s) => {
                          const nbInscrits = inscriptions.filter((i) => i.sessionId === s._id).length
                          const nbPayes = inscriptions.filter((i) => i.sessionId === s._id && i.statutPaiement === 'paye').length
                          const isPast = new Date(s.dateDebut) < new Date()
                          return (
                            <div key={s._id} className={`flex items-start justify-between gap-4 px-5 py-4 transition-all flex-wrap ${isPast ? 'opacity-60' : ''}`}>
                              <div className="flex-1 min-w-[240px]">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <span className="font-serif text-base text-[#f5f1ea]">
                                    <CalendarIcon className="w-3.5 h-3.5 inline mr-2 text-[#c9a96e]" />
                                    {formatDateTime(s.dateDebut)}
                                  </span>
                                  {s.dateFin && <span className="text-xs text-[#8a8580]">→ {formatDateTime(s.dateFin)}</span>}
                                  <span className={`text-[10px] px-2 py-0.5 border tracking-widest uppercase ${STATUT_COLORS[s.statut]}`}>
                                    {STATUT_LABELS[s.statut]}
                                  </span>
                                </div>
                                <div className="flex gap-4 flex-wrap text-xs">
                                  <span className="text-[#c9a96e]">
                                    <Users className="w-3 h-3 inline mr-1" />
                                    {s.placesMax - s.placesRestantes}/{s.placesMax} places prises
                                  </span>
                                  {s.lieu && <span className="text-[#8a8580]"><MapPin className="w-3 h-3 inline mr-1" />{s.lieu}</span>}
                                  <span className="text-[#8a8580]">{nbInscrits} inscrit{nbInscrits > 1 ? 's' : ''} · {nbPayes} payé{nbPayes > 1 ? 's' : ''}</span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => setEditingSession(s)} className="p-2 border border-[#c9a96e]/20 text-[#8a8580] hover:text-[#c9a96e] hover:border-[#c9a96e]/50 transition-all">
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => s._id && handleDelete('session', s._id, `la date du ${formatDate(s.dateDebut)}`)} className="p-2 border border-[#c9a96e]/20 text-[#8a8580] hover:text-red-400 hover:border-red-400/50 transition-all">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {tab === 'inscriptions' && (
          <div className="space-y-4">
            <h2 className="font-serif text-xl text-[#f5f1ea]">Inscriptions & paiements</h2>

            {inscriptions.length === 0 && (
              <div className="text-center py-16 border border-dashed border-[#c9a96e]/15">
                <Users className="w-8 h-8 text-[#8a8580] mx-auto mb-4" />
                <p className="text-[#8a8580] font-light">Aucune inscription pour l'instant</p>
              </div>
            )}

            <div className="grid gap-3">
              {inscriptions.map((i) => {
                const session = sessions.find((s) => s._id === i.sessionId)
                return (
                  <div key={i._id} className="border border-[#c9a96e]/15 p-5 hover:border-[#c9a96e]/40 transition-all">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-serif text-lg text-[#f5f1ea]">{i.nom}</h3>
                          <span className={`text-[10px] px-2 py-0.5 border tracking-widest uppercase ${PAIEMENT_COLORS[i.statutPaiement]}`}>
                            {PAIEMENT_LABELS[i.statutPaiement]}
                          </span>
                        </div>
                        <div className="flex gap-4 flex-wrap text-xs text-[#8a8580] mb-2">
                          <span><Mail className="w-3 h-3 inline mr-1" />{i.email}</span>
                          {i.telephone && <span><Phone className="w-3 h-3 inline mr-1" />{i.telephone}</span>}
                        </div>
                        <div className="flex gap-4 flex-wrap text-xs text-[#8a8580]">
                          <span>Session : {session ? `${session.accompagnementNom} · ${formatDate(session.dateDebut)}` : i.accompagnementNom || '—'}</span>
                          <span className="text-[#c9a96e]">{i.montant} CHF</span>
                          <span>Inscrit {formatDate(i.createdAt)}</span>
                        </div>
                        {i.message && (
                          <p className="text-xs text-[#f5f1ea]/60 italic mt-2 font-light leading-relaxed">"{i.message}"</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingInsc(i)} className="p-2 border border-[#c9a96e]/20 text-[#8a8580] hover:text-[#c9a96e] hover:border-[#c9a96e]/50 transition-all">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => i._id && handleDelete('insc', i._id, `l'inscription de ${i.nom}`)} className="p-2 border border-[#c9a96e]/20 text-[#8a8580] hover:text-red-400 hover:border-red-400/50 transition-all">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {tab === 'planning' && (
          <div className="space-y-4">
            <h2 className="font-serif text-xl text-[#f5f1ea]">Planning chronologique</h2>

            {sessionsPlanning.length === 0 && (
              <div className="text-center py-16 border border-dashed border-[#c9a96e]/15">
                <TrendingUp className="w-8 h-8 text-[#8a8580] mx-auto mb-4" />
                <p className="text-[#8a8580] font-light">Aucune session à afficher</p>
              </div>
            )}

            <div className="relative">
              {sessionsPlanning.map((s, idx) => {
                const isPast = new Date(s.dateDebut) < new Date()
                const nbInscrits = inscriptions.filter((i) => i.sessionId === s._id).length
                return (
                  <div key={s._id} className="flex gap-6 pb-8 relative">
                    {idx < sessionsPlanning.length - 1 && (
                      <div className="absolute left-[27px] top-14 bottom-0 w-px bg-[#c9a96e]/15" />
                    )}
                    <div className={`relative flex-shrink-0 w-14 h-14 rounded-full border-2 flex flex-col items-center justify-center ${
                      isPast ? 'border-[#8a8580]/30 text-[#8a8580]' : 'border-[#c9a96e] text-[#c9a96e] bg-gradient-to-br from-[#c9a96e]/10 to-transparent'
                    }`}>
                      <span className="text-[10px] tracking-widest uppercase">{new Date(s.dateDebut).toLocaleDateString('fr-FR', { month: 'short' })}</span>
                      <span className="font-serif text-sm">{new Date(s.dateDebut).getDate()}</span>
                    </div>
                    <div className={`flex-1 border p-4 transition-all ${isPast ? 'border-[#8a8580]/15 opacity-60' : 'border-[#c9a96e]/20 hover:border-[#c9a96e]/50'}`}>
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h3 className="font-serif text-lg text-[#f5f1ea]">{s.accompagnementNom}</h3>
                        <span className={`text-[10px] px-2 py-0.5 border tracking-widest uppercase ${STATUT_COLORS[s.statut]}`}>
                          {STATUT_LABELS[s.statut]}
                        </span>
                      </div>
                      <div className="flex gap-4 flex-wrap text-xs text-[#8a8580]">
                        <span>{formatDateTime(s.dateDebut)}</span>
                        {s.lieu && <span><MapPin className="w-3 h-3 inline mr-1" />{s.lieu}</span>}
                        <span className="text-[#c9a96e]">{s.placesMax - s.placesRestantes}/{s.placesMax} places · {nbInscrits} inscrit{nbInscrits > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modal Accompagnement */}
      {editingAcc && (
        <Modal onClose={() => setEditingAcc(null)} title={editingAcc._id ? 'Modifier l\'accompagnement' : 'Nouvel accompagnement'}>
          <div className="space-y-4">
            <Field label="Nom">
              <input type="text" value={editingAcc.nom || ''} onChange={(e) => setEditingAcc({ ...editingAcc, nom: e.target.value })} className="modal-input" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Durée">
                <input type="text" placeholder="1h30, 1 journée…" value={editingAcc.duree || ''} onChange={(e) => setEditingAcc({ ...editingAcc, duree: e.target.value })} className="modal-input" />
              </Field>
              <Field label="Prix (CHF)">
                <input type="number" step="0.01" value={editingAcc.prix ?? ''} onChange={(e) => setEditingAcc({ ...editingAcc, prix: parseFloat(e.target.value) || 0 })} className="modal-input" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Places max par défaut">
                <input type="number" value={editingAcc.placesMax ?? 1} onChange={(e) => setEditingAcc({ ...editingAcc, placesMax: parseInt(e.target.value) || 1 })} className="modal-input" />
                <p className="text-[10px] text-[#8a8580]/70 mt-1 font-light italic">
                  Valeur pré-remplie quand tu crées une nouvelle date. Modifiable date par date.
                </p>
              </Field>
              <Field label="Ordre d'affichage">
                <input type="number" value={editingAcc.ordre ?? 0} onChange={(e) => setEditingAcc({ ...editingAcc, ordre: parseInt(e.target.value) || 0 })} className="modal-input" />
              </Field>
            </div>
            <Field label="Description">
              <textarea rows={3} value={editingAcc.description || ''} onChange={(e) => setEditingAcc({ ...editingAcc, description: e.target.value })} className="modal-input resize-y" />
            </Field>
            <p className="text-[11px] text-[#8a8580]/70 font-light italic leading-relaxed border-l-2 border-[#c9a96e]/30 pl-3">
              Les dates de l'offre se gèrent uniquement via les <strong className="text-[#c9a96e]/80 not-italic">sessions</strong> (tu peux en ajouter autant que tu veux, chacune avec sa propre date et ses propres places).
            </p>
            <Field label="Lieu">
              <input
                type="text"
                placeholder="Adresse, salle, ou lien visio…"
                value={editingAcc.lieu || ''}
                onChange={(e) => setEditingAcc({ ...editingAcc, lieu: e.target.value })}
                className="modal-input"
              />
            </Field>
            <Field label="Instructions de préparation">
              <textarea
                rows={4}
                placeholder="Ce qu'il est bon d'apporter, comment se préparer, à quelle heure arriver…"
                value={editingAcc.instructionsPreparation || ''}
                onChange={(e) => setEditingAcc({ ...editingAcc, instructionsPreparation: e.target.value })}
                className="modal-input resize-y"
              />
            </Field>
            <Field label="Éléments inclus (un par ligne)">
              <textarea
                rows={4}
                value={(editingAcc.inclus || []).join('\n')}
                onChange={(e) => setEditingAcc({ ...editingAcc, inclus: e.target.value.split('\n').filter(Boolean) })}
                className="modal-input resize-y"
              />
            </Field>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-[#f5f1ea]/80 cursor-pointer">
                <input type="checkbox" checked={!!editingAcc.actif} onChange={(e) => setEditingAcc({ ...editingAcc, actif: e.target.checked })} className="accent-[#c9a96e]" />
                Actif (visible sur le site)
              </label>
              <label className="flex items-center gap-2 text-sm text-[#f5f1ea]/80 cursor-pointer">
                <input type="checkbox" checked={!!editingAcc.miseEnAvant} onChange={(e) => setEditingAcc({ ...editingAcc, miseEnAvant: e.target.checked })} className="accent-[#c9a96e]" />
                Mettre en avant
              </label>
            </div>
          </div>
          <ModalActions onCancel={() => setEditingAcc(null)} onSave={handleSaveAcc} />
        </Modal>
      )}

      {/* Modal Session */}
      {editingSession && (
        <Modal onClose={() => setEditingSession(null)} title={editingSession._id ? 'Modifier la date' : 'Nouvelle date'}>
          <div className="space-y-4">
            <p className="text-xs text-[#8a8580] font-light italic leading-relaxed border-l-2 border-[#c9a96e]/40 pl-3">
              Chaque date est indépendante. Le nombre de places se gère ici, date par date.
            </p>
            <Field label="Accompagnement">
              <select
                value={editingSession.accompagnementId || ''}
                onChange={(e) => {
                  const acc = accompagnements.find((a) => a._id === e.target.value)
                  setEditingSession({
                    ...editingSession,
                    accompagnementId: e.target.value,
                    accompagnementNom: acc?.nom || '',
                    lieu: editingSession.lieu || acc?.lieu || '',
                    placesMax: editingSession._id ? editingSession.placesMax : (acc?.placesMax || 1),
                    placesRestantes: editingSession._id ? editingSession.placesRestantes : (acc?.placesMax || 1),
                  })
                }}
                className="modal-input"
              >
                <option value="">— Choisir —</option>
                {accompagnements.map((a) => (
                  <option key={a._id} value={a._id}>{a.nom}</option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date de début">
                <DateTimePicker
                  value={editingSession.dateDebut}
                  onChange={(iso) => setEditingSession({ ...editingSession, dateDebut: iso })}
                  placeholder="Choisir une date…"
                  allowClear={false}
                />
              </Field>
              <Field label="Date de fin (optionnel)">
                <DateTimePicker
                  value={editingSession.dateFin}
                  onChange={(iso) => setEditingSession({ ...editingSession, dateFin: iso })}
                  placeholder="Facultatif"
                  minDate={editingSession.dateDebut ? new Date(editingSession.dateDebut) : undefined}
                />
              </Field>
            </div>
            <Field label="Lieu (spécifique à cette date)">
              <input type="text" placeholder="Adresse, salle ou lien visio…" value={editingSession.lieu || ''} onChange={(e) => setEditingSession({ ...editingSession, lieu: e.target.value })} className="modal-input" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Places disponibles">
                <input
                  type="number"
                  min={1}
                  value={editingSession.placesMax ?? 1}
                  onChange={(e) => {
                    const v = parseInt(e.target.value) || 1
                    setEditingSession((prev) => prev ? {
                      ...prev,
                      placesMax: v,
                      // si nouvelle session, align placesRestantes
                      placesRestantes: prev._id ? prev.placesRestantes : v,
                    } : prev)
                  }}
                  className="modal-input"
                />
              </Field>
              <Field label="Places restantes">
                <input
                  type="number"
                  min={0}
                  value={editingSession.placesRestantes ?? 1}
                  onChange={(e) => setEditingSession({ ...editingSession, placesRestantes: parseInt(e.target.value) || 0 })}
                  className="modal-input"
                  disabled={!editingSession._id}
                />
              </Field>
            </div>
            {!editingSession._id && (
              <p className="text-[11px] text-[#8a8580]/70 font-light italic">
                « Places restantes » s'aligne automatiquement sur « Places disponibles » pour une nouvelle date.
              </p>
            )}
            <Field label="Statut">
              <select value={editingSession.statut || 'ouverte'} onChange={(e) => setEditingSession({ ...editingSession, statut: e.target.value as Session['statut'] })} className="modal-input">
                <option value="ouverte">Ouverte</option>
                <option value="complete">Complète</option>
                <option value="annulee">Annulée</option>
                <option value="terminee">Terminée</option>
              </select>
            </Field>
            <Field label="Notes (internes)">
              <textarea rows={2} value={editingSession.notes || ''} onChange={(e) => setEditingSession({ ...editingSession, notes: e.target.value })} className="modal-input resize-y" />
            </Field>
          </div>
          <div className="flex items-center justify-end gap-3 mt-8 pt-4 border-t border-[#c9a96e]/15 flex-wrap">
            <button onClick={() => setEditingSession(null)} className="px-5 py-2.5 border border-[#c9a96e]/20 text-[#8a8580] hover:text-[#f5f1ea] text-xs tracking-widest uppercase transition-all">
              Annuler
            </button>
            {!editingSession._id && (
              <button
                onClick={() => handleSaveSession(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e]/10 text-xs tracking-widest uppercase font-semibold transition-all"
                title="Enregistrer et préparer une autre date"
              >
                <CalendarPlus className="w-3.5 h-3.5" /> Enregistrer et ajouter une autre
              </button>
            )}
            <button onClick={() => handleSaveSession(false)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#c9a96e] text-[#0a0a0a] hover:bg-[#d4b47a] text-xs tracking-widest uppercase font-semibold transition-all">
              <Save className="w-3.5 h-3.5" /> Enregistrer
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Inscription */}
      {editingInsc && (
        <Modal onClose={() => setEditingInsc(null)} title="Modifier l'inscription">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Nom">
                <input type="text" value={editingInsc.nom || ''} onChange={(e) => setEditingInsc({ ...editingInsc, nom: e.target.value })} className="modal-input" />
              </Field>
              <Field label="Email">
                <input type="email" value={editingInsc.email || ''} onChange={(e) => setEditingInsc({ ...editingInsc, email: e.target.value })} className="modal-input" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Téléphone">
                <input type="tel" value={editingInsc.telephone || ''} onChange={(e) => setEditingInsc({ ...editingInsc, telephone: e.target.value })} className="modal-input" />
              </Field>
              <Field label="Montant (CHF)">
                <input type="number" step="0.01" value={editingInsc.montant ?? 0} onChange={(e) => setEditingInsc({ ...editingInsc, montant: parseFloat(e.target.value) || 0 })} className="modal-input" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Statut paiement">
                <select value={editingInsc.statutPaiement || 'en_attente'} onChange={(e) => setEditingInsc({ ...editingInsc, statutPaiement: e.target.value as Inscription['statutPaiement'] })} className="modal-input">
                  <option value="en_attente">En attente</option>
                  <option value="paye">Payé</option>
                  <option value="rembourse">Remboursé</option>
                  <option value="annule">Annulé</option>
                </select>
              </Field>
              <Field label="Mode paiement">
                <select value={editingInsc.modePaiement || ''} onChange={(e) => setEditingInsc({ ...editingInsc, modePaiement: e.target.value as Inscription['modePaiement'] })} className="modal-input">
                  <option value="">—</option>
                  <option value="cb">Carte bancaire</option>
                  <option value="virement">Virement</option>
                  <option value="especes">Espèces</option>
                  <option value="autre">Autre</option>
                </select>
              </Field>
            </div>
            <Field label="Message de l'inscrit">
              <textarea rows={2} value={editingInsc.message || ''} onChange={(e) => setEditingInsc({ ...editingInsc, message: e.target.value })} className="modal-input resize-y" readOnly />
            </Field>
            <Field label="Notes admin">
              <textarea rows={2} value={editingInsc.notes || ''} onChange={(e) => setEditingInsc({ ...editingInsc, notes: e.target.value })} className="modal-input resize-y" />
            </Field>
          </div>
          <ModalActions onCancel={() => setEditingInsc(null)} onSave={handleSaveInsc} />
        </Modal>
      )}

      <style>{`
        .modal-input {
          width: 100%;
          background: #0a0a0a;
          border: 1px solid rgba(201, 169, 110, 0.2);
          color: #f5f1ea;
          padding: 10px 14px;
          font-size: 14px;
          font-weight: 300;
          outline: none;
          transition: border-color 0.2s;
        }
        .modal-input:focus { border-color: #c9a96e; }
      `}</style>
    </div>
  )
}

function StatCard({ icon: Icon, label, value, highlight }: { icon: any; label: string; value: any; highlight?: boolean }) {
  return (
    <div className={`border p-5 bg-gradient-to-br from-[#c9a96e]/5 to-transparent ${
      highlight ? 'border-amber-400/40' : 'border-[#c9a96e]/15'
    }`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-3.5 h-3.5 ${highlight ? 'text-amber-400' : 'text-[#c9a96e]'}`} />
        <p className="text-[10px] tracking-widest uppercase text-[#8a8580]">{label}</p>
      </div>
      <p className={`font-serif text-2xl ${highlight ? 'text-amber-400' : 'text-[#f5f1ea]'}`}>{value}</p>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] tracking-widest uppercase text-[#8a8580] mb-2">{label}</label>
      {children}
    </div>
  )
}

function Modal({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-[#c9a96e]/30">
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-[#c9a96e]/15 bg-[#0a0a0a]">
          <h3 className="font-serif text-xl text-[#f5f1ea]">{title}</h3>
          <button onClick={onClose} className="p-2 text-[#8a8580] hover:text-[#c9a96e] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

function ModalActions({ onCancel, onSave }: { onCancel: () => void; onSave: () => void }) {
  return (
    <div className="flex items-center justify-end gap-3 mt-8 pt-4 border-t border-[#c9a96e]/15">
      <button onClick={onCancel} className="px-5 py-2.5 border border-[#c9a96e]/20 text-[#8a8580] hover:text-[#f5f1ea] text-xs tracking-widest uppercase transition-all">
        Annuler
      </button>
      <button onClick={onSave} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#c9a96e] text-[#0a0a0a] hover:bg-[#d4b47a] text-xs tracking-widest uppercase font-semibold transition-all">
        <Save className="w-3.5 h-3.5" /> Enregistrer
      </button>
    </div>
  )
}
